/**
 * Travel List PDF Layout
 *
 * Creates a simple table layout for travel arrangements.
 * Shows essential travel details in chronological order.
 */

export class TravelListPDFLayout {
  constructor() {
    this.layoutType = 'table';
    this.name = 'Travel List Layout';
  }

  /**
   * Generate PDF content structure for travel list
   * @param {Object} pageContext - Page context with travel data
   * @returns {Object} PDF content structure
   */
  async generate(pageContext) {
    const travelData = Array.isArray(pageContext.data) ? pageContext.data : [];

    if (travelData.length === 0) {
      return this.generateEmptyContent(pageContext);
    }

    // Sort travel chronologically
    const sortedTravel = this.sortTravelChronologically(travelData);

    return {
      sections: [
        this.generateTravelListSection(sortedTravel, pageContext)
      ].filter(section => section !== null)
    };
  }

  /**
   * Generate simple travel list section
   * @param {Array} travelData - Sorted travel data
   * @param {Object} pageContext - Page context
   * @returns {Object} Travel list section
   */
  generateTravelListSection(travelData, pageContext) {
    return {
      type: 'table',
      title: null, // No title - clean simple table
      columns: ['Date/Time', 'Type', 'Route', 'Details'],
      rows: travelData.map(travel => [
        this.formatTravelDateTime(travel),
        this.formatTravelType(travel.type),
        this.formatRoute(travel),
        this.formatTravelDetails(travel)
      ])
    };
  }

  /**
   * Generate chronological travel timeline
   * @param {Array} chronologicalTravel - Travel sorted by date
   * @param {Object} pageContext - Page context
   * @returns {Object} Timeline section
   */
  generateChronologicalSection(chronologicalTravel, pageContext) {
    return {
      type: 'table',
      title: 'Travel Timeline',
      columns: ['Date/Time', 'Type', 'Route', 'Duration', 'Status'],
      rows: chronologicalTravel.map(travel => [
        this.formatTravelDateTime(travel),
        this.formatTravelType(travel.type),
        this.formatRoute(travel),
        this.calculateDuration(travel.departureTime, travel.arrivalTime),
        travel.status || 'Booked'
      ])
    };
  }

  /**
   * Generate travel type groups (Flights, Trains, etc.)
   * @param {Object} groupedTravel - Travel grouped by type
   * @param {Object} pageContext - Page context
   * @returns {Array} Travel type sections
   */
  generateTravelTypeGroups(groupedTravel, pageContext) {
    const sections = [];

    Object.entries(groupedTravel).forEach(([type, travels]) => {
      if (travels.length > 0) {
        sections.push(this.generateTravelTypeSection(type, travels));
      }
    });

    return sections;
  }

  /**
   * Generate section for specific travel type
   * @param {string} type - Travel type
   * @param {Array} travels - Travel items of this type
   * @returns {Object} Travel type section
   */
  generateTravelTypeSection(type, travels) {
    const columns = this.getTravelTypeColumns(type);
    const rows = travels.map(travel => this.formatTravelTypeRow(travel, type));

    return {
      type: 'table',
      title: this.formatTravelTypeTitle(type),
      columns: columns,
      rows: rows
    };
  }

  /**
   * Get appropriate columns for travel type
   * @param {string} type - Travel type
   * @returns {Array} Column headers
   */
  getTravelTypeColumns(type) {
    switch (type.toLowerCase()) {
      case 'flight':
        return ['Date', 'Flight', 'Route', 'Duration', 'Terminal', 'Confirmation'];
      case 'train':
        return ['Date', 'Train', 'Route', 'Duration', 'Platform', 'Confirmation'];
      case 'bus':
        return ['Date', 'Bus Line', 'Route', 'Duration', 'Stop', 'Confirmation'];
      default:
        return ['Date', 'Service', 'Route', 'Duration', 'Details', 'Confirmation'];
    }
  }

  /**
   * Format travel type row data
   * @param {Object} travel - Travel item
   * @param {string} type - Travel type
   * @returns {Array} Row data
   */
  formatTravelTypeRow(travel, type) {
    const baseData = [
      this.formatTravelDate(travel.departureTime),
      travel.flightNumber || travel.trainNumber || travel.busNumber || travel.serviceNumber || '-',
      this.formatRoute(travel),
      this.calculateDuration(travel.departureTime, travel.arrivalTime),
    ];

    switch (type.toLowerCase()) {
      case 'flight':
        return [
          ...baseData,
          travel.departureTerminal || travel.terminal || '-',
          travel.confirmationNumber || '-'
        ];
      case 'train':
        return [
          ...baseData,
          travel.platform || '-',
          travel.confirmationNumber || '-'
        ];
      case 'bus':
        return [
          ...baseData,
          travel.busStop || travel.stop || '-',
          travel.confirmationNumber || '-'
        ];
      default:
        return [
          ...baseData,
          travel.details || '-',
          travel.confirmationNumber || '-'
        ];
    }
  }

  /**
   * Generate confirmations section
   * @param {Array} travelData - Travel data
   * @param {Object} pageContext - Page context
   * @returns {Object|null} Confirmations section or null
   */
  generateConfirmationsSection(travelData, pageContext) {
    const travelWithConfirmations = travelData.filter(travel =>
      travel.confirmationNumber || travel.bookingReference
    );

    if (travelWithConfirmations.length === 0) {
      return null;
    }

    return {
      type: 'table',
      title: 'Booking Confirmations',
      columns: ['Date', 'Type', 'Route', 'Confirmation Number', 'Booking Reference'],
      rows: travelWithConfirmations.map(travel => [
        this.formatTravelDate(travel.departureTime),
        this.formatTravelType(travel.type),
        this.formatRoute(travel),
        travel.confirmationNumber || '-',
        travel.bookingReference || '-'
      ])
    };
  }

  /**
   * Format travel details for display
   * @param {Object} travel - Travel object
   * @returns {string} Formatted details
   */
  formatTravelDetails(travel) {
    const details = [];

    // Add flight/train/bus number if available
    const serviceNumber = travel.details?.flightNumber || travel.details?.trainNumber || travel.details?.busNumber || travel.details?.serviceNumber;
    if (serviceNumber) {
      details.push(serviceNumber);
    }

    // Add airline/operator if available
    if (travel.details?.airline) {
      details.push(travel.details.airline);
    }

    // Add confirmation number if available
    if (travel.details?.bookingReference) {
      details.push(`Conf: ${travel.details.bookingReference}`);
    }

    // Add duration if calculable
    const duration = this.calculateDuration(travel.details?.departureTime, travel.details?.arrivalTime);
    if (duration && duration !== '0h 0m') {
      details.push(duration);
    }

    return details.length > 0 ? details.join(' | ') : '-';
  }

  /**
   * Generate content for empty travel list
   * @param {Object} pageContext - Page context
   * @returns {Object} Empty content structure
   */
  generateEmptyContent(pageContext) {
    return {
      sections: [{
        type: 'text',
        title: 'No Travel Arrangements',
        content: 'This trip does not have any travel arrangements added yet. Add some flights, trains, or bus bookings to generate a more detailed PDF export.'
      }]
    };
  }


  /**
   * Sort all travel chronologically
   * @param {Array} travelData - Travel data
   * @returns {Array} Sorted travel data
   */
  sortTravelChronologically(travelData) {
    return travelData
      .filter(travel => travel.details?.departureTime)
      .sort((a, b) => new Date(a.details.departureTime) - new Date(b.details.departureTime));
  }

  /**
   * Format travel type for display
   * @param {string} type - Travel type
   * @returns {string} Formatted type
   */
  formatTravelType(type) {
    if (!type) return 'Other';
    return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
  }

  /**
   * Format travel type title for display
   * @param {string} type - Travel type
   * @returns {string} Formatted title
   */
  formatTravelTypeTitle(type) {
    const titles = {
      flight: 'Flight Details',
      train: 'Train Details',
      bus: 'Bus Details',
      other: 'Other Transportation'
    };

    return titles[type.toLowerCase()] || 'Transportation Details';
  }

  /**
   * Format route for display
   * @param {Object} travel - Travel object
   * @returns {string} Formatted route
   */
  formatRoute(travel) {
    const origin = travel.details?.departureCity || travel.details?.origin || travel.details?.from || 'Unknown';
    const destination = travel.details?.arrivalCity || travel.details?.destination || travel.details?.to || 'Unknown';
    return `${origin} → ${destination}`;
  }

  /**
   * Format travel date and time
   * @param {Object} travel - Travel object
   * @returns {string} Formatted date time
   */
  formatTravelDateTime(travel) {
    const departureDateTime = travel.details?.departureTime;
    const departureDate = this.formatTravelDate(departureDateTime);
    const departureTime = this.formatTime(departureDateTime);
    return `${departureDate} ${departureTime}`;
  }

  /**
   * Format travel date
   * @param {string} dateTimeString - Date time string
   * @returns {string} Formatted date
   */
  formatTravelDate(dateTimeString) {
    if (!dateTimeString) return 'Unknown';

    try {
      const date = new Date(dateTimeString);
      return date.toLocaleDateString();
    } catch (error) {
      return dateTimeString;
    }
  }

  /**
   * Format time
   * @param {string} dateTimeString - Date time string
   * @returns {string} Formatted time
   */
  formatTime(dateTimeString) {
    if (!dateTimeString) return '';

    try {
      const date = new Date(dateTimeString);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return '';
    }
  }

  /**
   * Calculate duration between two times
   * @param {string} startTime - Start time
   * @param {string} endTime - End time
   * @returns {string} Formatted duration
   */
  calculateDuration(startTime, endTime) {
    const minutes = this.calculateDurationMinutes(startTime, endTime);
    return this.formatDuration(minutes);
  }

  /**
   * Calculate duration in minutes
   * @param {string} startTime - Start time
   * @param {string} endTime - End time
   * @returns {number} Duration in minutes
   */
  calculateDurationMinutes(startTime, endTime) {
    if (!startTime || !endTime) return 0;

    try {
      const start = new Date(startTime);
      const end = new Date(endTime);
      return Math.max(0, Math.round((end - start) / (1000 * 60)));
    } catch (error) {
      return 0;
    }
  }

  /**
   * Format duration from minutes
   * @param {number} minutes - Duration in minutes
   * @returns {string} Formatted duration
   */
  formatDuration(minutes) {
    if (minutes <= 0) return '0h 0m';

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  }

  /**
   * Get preview information for this layout
   * @param {*} data - Data to analyze
   * @returns {Object} Preview information
   */
  getPreview(data) {
    const travelCount = Array.isArray(data) ? data.length : 0;

    return {
      estimatedPages: Math.max(1, Math.ceil(travelCount / 10)), // Simple table estimate
      sections: ['Travel List'], // Only one simple table
      features: [
        'Chronological order',
        'Essential travel details',
        'Service numbers and confirmations',
        'Duration calculations'
      ],
      dataCount: travelCount
    };
  }
}